"use client";

import React, { useState, useRef, useEffect } from "react";
import { ChevronsLeftRight, MoveRight } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
interface BeforeAfterSliderProps {
  beforeImage: string;
  afterImage: string;
  beforeAlt?: string;
  afterAlt?: string;
}
function BeforeAfterSlider({
  beforeImage,
  afterImage,
  beforeAlt = "Before renovation",
  afterAlt = "After renovation"
}: BeforeAfterSliderProps) {
  const [position, setPosition] = useState(50);
  const sliderRef = useRef<HTMLDivElement>(null);
  const handleInteraction = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if (!sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    let clientX: number;
    if ("touches" in e) {
      clientX = e.touches[0].clientX;
    } else {
      clientX = (e as React.MouseEvent).clientX;
    }
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percent = x / rect.width * 100;
    setPosition(percent);
  };
  return;
}
const projects = [{
  id: 1,
  category: "Bathroom Remodeling",
  title: "From Outdated to Outstanding",
  description: "We transformed this cramped, 90s-era bathroom into a spacious, spa-like retreat. Featuring a custom walk-in shower with bench seating, dual vanities with quartz countertops, and elegant brushed nickel fixtures.",
  beforeImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1576980/img/bathroom_before_1.png",
  afterImg: "https://amazing-satyr.10web.cloud/wp-content/uploads/2026/06/image3-15-scaled.jpeg?v=bieagaezdoj"
}, {
  id: 2,
  category: "Kitchen Remodeling",
  title: "A Chef's Dream Realized",
  description: "By removing a load-bearing wall, we opened up this dark kitchen to the living area. The new layout boasts custom shaker cabinets, a massive 10-foot island perfect for entertaining, and professional-grade appliances.",
  beforeImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1576980/img/kitchen_before_1.png",
  afterImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1576980/img/kitchen_after_1.png"
}, {
  id: 3,
  category: "Basement Remodeling",
  title: "Ultimate Lower Level Living",
  description: "What was once a cold, unfinished storage space is now the family's favorite gathering spot. Complete with a custom wet bar, luxury vinyl plank flooring, and a fully equipped home theater area.",
  beforeImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1576980/img/basement_before_1.png",
  afterImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1576980/img/basement_after_1.png",
  beforeImgClassName: "p-2 rounded-2xl bg-card border border-border/50 shadow-2xl shadow-black/50 !bg-[url(https://amazing-satyr.10web.cloud/wp-content/uploads/2026/03/project_luxury_bath.webp)]"
}];
export default function BeforeAndAfter() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: carouselRef,
    isVisible: isCarouselVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="before-and-after" className="dark bg-background text-foreground py-24 md:py-32 overflow-hidden relative">
      {}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1576980/img/noise_texture.png')] mix-blend-overlay"></div>

      
    </section>;
}